// Tier-1 glossary detail content loader.
//
// Architecture (A1): lazy glob + dynamic-imported marked/js-yaml/glossary,
// so neither MD bodies nor the markdown engine reach the main bundle.

import type { Marked as MarkedClass } from "marked";
import { pathMap, type Locale } from "./locale";

export type GlossaryDetailFrontmatter = {
  seoTitle?: string;
  metaDescription?: string;
  readingTimeMinutes?: number;
};

export type GlossaryHeading = { id: string; text: string; level: 2 | 3 };

export type GlossaryDetail = {
  fm: GlossaryDetailFrontmatter;
  bodyHtml: string;
  headings: GlossaryHeading[];
};

const RAW_FILES = import.meta.glob("/content/glossary/**/*.md", {
  query: "?raw",
  import: "default",
}) as Record<string, () => Promise<string>>;

// ---------------------------------------------------------------------------
// Lazy markdown engine
// ---------------------------------------------------------------------------
type Yaml = { load: (s: string) => unknown };
type Engine = {
  Marked: typeof MarkedClass;
  yaml: Yaml;
  getGlossaryEntryById: (id: string) => { id: string; tier: 1 | 2; slug: { de: string; en: string } } | undefined;
};

let enginePromise: Promise<Engine> | null = null;
function loadEngine(): Promise<Engine> {
  if (!enginePromise) {
    enginePromise = Promise.all([
      import("marked"),
      import("js-yaml"),
      import("./glossary"),
    ]).then(([markedMod, yamlMod, glossaryMod]) => ({
      Marked: markedMod.Marked,
      yaml: (yamlMod as unknown as { default?: Yaml }).default ?? (yamlMod as unknown as Yaml),
      getGlossaryEntryById: glossaryMod.getGlossaryEntryById,
    }));
  }
  return enginePromise;
}

function slugify(text: string): string {
  return text
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/ä/g, "a").replace(/ö/g, "o").replace(/ü/g, "u").replace(/ß/g, "ss")
    .replace(/[^a-z0-9\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");
}

function parseFrontmatter(raw: string, yaml: Yaml): { data: Record<string, unknown>; content: string } {
  const normalized = raw.replace(/^\uFEFF/, "").replace(/\r\n/g, "\n");
  if (!normalized.startsWith("---\n")) return { data: {}, content: normalized };
  const end = normalized.indexOf("\n---", 4);
  if (end === -1) return { data: {}, content: normalized };
  const yamlBlock = normalized.slice(4, end);
  const rest = normalized.slice(end + 4).replace(/^\n/, "");
  const data = (yaml.load(yamlBlock) ?? {}) as Record<string, unknown>;
  return { data, content: rest };
}

function buildMarked(locale: Locale, engine: Engine) {
  const headings: GlossaryHeading[] = [];
  const seen = new Map<string, number>();
  const glossaryBase = pathMap.glossary[locale];
  const marked = new engine.Marked({ gfm: true, breaks: false });
  marked.use({
    renderer: {
      heading({ tokens, depth }) {
        const text = this.parser.parseInline(tokens);
        const plain = tokens.map((t) => ("text" in t ? (t as { text: string }).text : "")).join("");
        let id = slugify(plain);
        const count = seen.get(id) ?? 0;
        seen.set(id, count + 1);
        if (count > 0) id = `${id}-${count + 1}`;
        if (depth === 2 || depth === 3) headings.push({ id, text: plain, level: depth });
        return `<h${depth} id="${id}">${text}</h${depth}>\n`;
      },
      link({ href, title, tokens }) {
        const text = this.parser.parseInline(tokens);
        if (href?.startsWith("glossary:")) {
          const id = href.slice("glossary:".length);
          const entry = engine.getGlossaryEntryById(id);
          if (entry) {
            const resolved =
              entry.tier === 1
                ? `${glossaryBase}/${entry.slug[locale]}`
                : `${glossaryBase}#${entry.id}`;
            const titleAttr = title ? ` title="${title}"` : "";
            return `<a href="${resolved}" data-glossary="${id}"${titleAttr}>${text}</a>`;
          }
          // eslint-disable-next-line no-console
          console.warn(`[glossary-content] Unknown glossary id "${id}" referenced in link`);
          return text;
        }
        const titleAttr = title ? ` title="${title}"` : "";
        return `<a href="${href ?? ""}"${titleAttr}>${text}</a>`;
      },
    },
  });
  return { marked, getHeadings: () => headings };
}

export async function getGlossaryDetail(id: string, locale: Locale): Promise<GlossaryDetail | null> {
  const path = `/content/glossary/${id}/${locale}.md`;
  const loader = RAW_FILES[path];
  if (!loader) return null;
  const [raw, engine] = await Promise.all([loader(), loadEngine()]);
  const parsed = parseFrontmatter(raw, engine.yaml);
  const { marked, getHeadings } = buildMarked(locale, engine);
  const bodyHtml = marked.parse(parsed.content) as string;
  return { fm: parsed.data as GlossaryDetailFrontmatter, bodyHtml, headings: getHeadings() };
}

export function formatGlossaryDate(iso: string, locale: Locale): string {
  const d = new Date(iso);
  return new Intl.DateTimeFormat(locale === "de" ? "de-DE" : "en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  }).format(d);
}
